import { useState, type FormEvent } from 'react'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import type { PerfilEquipo, Seniority } from '@/lib/types'
import { PageHeader } from '@/components/ui'
import { IconPlus } from '@/components/icons/Icons'

const empty: Omit<PerfilEquipo, 'id'> = { id_perfil: '', rol_comercial: '', seniority: 'Senior', costo_interno_hora: 0, precio_venta_hora: 0, margen_base: 0 }

export default function Perfiles() {
  const { data: items = [], isLoading: loading } = useCachedQuery<PerfilEquipo>('perfiles_equipo')
  const { addMut, updateMut, removeMut } = useCachedMutations('perfiles_equipo')
  const [modal, setModal] = useState(false)
  const [edit, setEdit] = useState<PerfilEquipo | null>(null)
  const [form, setForm] = useState(empty)
  const [saving, setSaving] = useState(false)

  const calcMargen = (c: number, p: number) => p > 0 ? parseFloat(((p - c) / p * 100).toFixed(1)) : 0

  const openNew = () => { setEdit(null); setForm(empty); setModal(true) }
  const openEdit = (p: PerfilEquipo) => { setEdit(p); setForm({ id_perfil: p.id_perfil, rol_comercial: p.rol_comercial, seniority: p.seniority, costo_interno_hora: p.costo_interno_hora, precio_venta_hora: p.precio_venta_hora, margen_base: p.margen_base }); setModal(true) }

  const handleDelete = async (p: PerfilEquipo) => {
    if (!confirm(`¿Eliminar perfil ${p.rol_comercial} ${p.seniority}?`)) return
    try {
      await removeMut.mutateAsync(p.id)
      showToast('Perfil eliminado', 'success')
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al eliminar', 'error') }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.id_perfil.trim() || !form.rol_comercial.trim()) { showToast('ID y Rol son requeridos', 'error'); return }
    const data = { ...form, margen_base: calcMargen(form.costo_interno_hora, form.precio_venta_hora) }
    setSaving(true)
    try {
      if (edit) { await updateMut.mutateAsync({ id: edit.id, ...data } as Record<string, unknown>); showToast('Perfil actualizado', 'success') }
      else { await addMut.mutateAsync(data as unknown as Record<string, unknown>); showToast('Perfil creado', 'success') }
      setModal(false)
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al guardar', 'error') } finally { setSaving(false) }
  }

  const margenColor = (m: number) => m >= 50 ? 'var(--success)' : m >= 30 ? 'var(--warning)' : 'var(--danger)'

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Perfiles' }]} /><div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Perfiles' }]}
        meta={`${items.length} configurado${items.length !== 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary" onClick={openNew} id="btn-new-profile">
            <IconPlus size={16} /> Nuevo Perfil
          </button>
        }
      />

      {items.length === 0 ? (
        <div className="empty-state"><h3>Sin perfiles</h3><p>Configura perfiles de equipo para cotizar equipos dedicados.</p><button className="btn btn-primary" onClick={openNew}>Crear Perfil</button></div>
      ) : (
        <div className="table-container"><table><thead><tr><th>ID</th><th>Rol</th><th>Seniority</th><th>Costo/h</th><th>Venta/h</th><th>Margen</th><th style={{ width: 80 }}>Acciones</th></tr></thead>
          <tbody>{items.map(p => (
            <tr key={p.id}>
              <td><code style={{ background: 'var(--bg-card-hover)', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem' }}>{p.id_perfil}</code></td>
              <td style={{ fontWeight: 600 }}>{p.rol_comercial}</td>
              <td><span className={`badge ${p.seniority === 'Senior' ? 'badge-aceptada' : p.seniority === 'Semi-Senior' ? 'badge-enviada' : 'badge-borrador'}`}>{p.seniority}</span></td>
              <td>${p.costo_interno_hora}</td>
              <td style={{ fontWeight: 600 }}>${p.precio_venta_hora}</td>
              <td style={{ fontWeight: 700, color: margenColor(p.margen_base) }}>{p.margen_base}%</td>
              <td style={{ display: 'flex', gap: 4 }}><button className="btn btn-sm btn-secondary" onClick={() => openEdit(p)} title="Editar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button><button className="btn btn-sm btn-danger" onClick={() => handleDelete(p)} title="Eliminar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button></td>
            </tr>
          ))}</tbody></table></div>
      )}

      <Modal isOpen={modal} onClose={() => setModal(false)} title={edit ? 'Editar Perfil' : 'Nuevo Perfil'}>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">ID Perfil *</label><input className="form-input" value={form.id_perfil} onChange={e => setForm(f => ({ ...f, id_perfil: e.target.value.toUpperCase() }))} placeholder="DEV-FS-SR" required autoFocus /></div>
            <div className="form-group"><label className="form-label">Seniority</label><select className="form-select" value={form.seniority} onChange={e => setForm(f => ({ ...f, seniority: e.target.value as Seniority }))}><option value="Junior">Junior</option><option value="Semi-Senior">Semi-Senior</option><option value="Senior">Senior</option></select></div>
          </div>
          <div className="form-group"><label className="form-label">Rol Comercial *</label><input className="form-input" value={form.rol_comercial} onChange={e => setForm(f => ({ ...f, rol_comercial: e.target.value }))} placeholder="Desarrollador Fullstack" required /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Costo Interno ($/h)</label><input className="form-input" type="number" step="0.5" min="0" value={form.costo_interno_hora} onChange={e => setForm(f => ({ ...f, costo_interno_hora: parseFloat(e.target.value) || 0 }))} /></div>
            <div className="form-group"><label className="form-label">Precio Venta ($/h)</label><input className="form-input" type="number" step="0.5" min="0" value={form.precio_venta_hora} onChange={e => setForm(f => ({ ...f, precio_venta_hora: parseFloat(e.target.value) || 0 }))} /></div>
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--bg-card-hover)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>Margen calculado: </span>
            <strong style={{ color: margenColor(calcMargen(form.costo_interno_hora, form.precio_venta_hora)) }}>{calcMargen(form.costo_interno_hora, form.precio_venta_hora)}%</strong>
          </div>
          <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : edit ? 'Actualizar' : 'Crear'}</button></div>
        </form>
      </Modal>
    </div>
  )
}
